/**
 * SkeletonCard - Prebuilt skeleton for card layouts with Framer Motion animations
 *
 * Provides a ready-to-use skeleton loader for card-based content with smooth animations.
 *
 * @example
 * <SkeletonCard />
 *
 * @example
 * <SkeletonCard hasImage hasFooter animation="wave" />
 */

import React from 'react';
import PropTypes from 'prop-types';
import Skeleton from './Skeleton';
import { cn } from '../utils/classNames';

const SkeletonCard = ({
  hasImage = false,
  hasFooter = false,
  className,
  animation = 'shimmer',
  duration = 1.5,
}) => {
  return (
    <div
      className={cn('bg-white rounded-lg shadow p-6 animate-fade-in', className)}
    >
      {/* Image placeholder */}
      {hasImage && (
        <Skeleton
          variant="rectangle"
          height="200px"
          className="mb-4"
          animation={animation}
          duration={duration}
        />
      )}

      {/* Header */}
      <div className="space-y-3">
        <Skeleton
          variant="text"
          width="70%"
          height="24px"
          animation={animation}
          duration={duration}
        />
        <Skeleton
          variant="text"
          width="40%"
          height="16px"
          animation={animation}
          duration={duration}
        />
      </div>

      {/* Body */}
      <div className="mt-4 space-y-2">
        <Skeleton
          variant="text"
          count={2}
          spacing="sm"
          animation={animation}
          duration={duration}
        />
      </div>

      {/* Footer */}
      {hasFooter && (
        <div className="mt-6 flex gap-2">
          <Skeleton
            variant="button"
            width="100px"
            animation={animation}
            duration={duration}
          />
          <Skeleton
            variant="button"
            width="100px"
            animation={animation}
            duration={duration}
          />
        </div>
      )}
    </div>
  );
};

SkeletonCard.propTypes = {
  /** Show image placeholder */
  hasImage: PropTypes.bool,
  /** Show footer buttons */
  hasFooter: PropTypes.bool,
  /** Additional CSS classes */
  className: PropTypes.string,
  /** Animation type (pulse, shimmer, wave, fade) */
  animation: PropTypes.oneOf(['pulse', 'shimmer', 'wave', 'fade']),
  /** Animation duration in seconds */
  duration: PropTypes.number,
};

export default SkeletonCard;
